/**
 * Navigation Hook for Next.js Portal (Optimized v2)
 * 
 * OPTIMIZATIONS:
 * 1. Initializes from localStorage SYNCHRONOUSLY before first render completes
 * 2. Never shows loading state if we have cached data
 * 3. Background refresh only - non-blocking
 */

'use client'

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { normalizeAdminRole } from '@/lib/auth-core';
import { useAuth } from './useAuth'
import { NAVIGATION_CACHE_VERSION, navigationService, type NavigationGroup, type NavigationItem, type NavigationResponse } from '@/lib/navigationService'
import { clientService, type ClientInfo } from '@/lib/clientService'
import { getDemoAwareExpectedClientId, isOperationsDemoTransitionActive } from '@/lib/demo/clientContext'
import { ApiError } from '@/lib/apiService'
import { isSilentFetchError } from '@/lib/capacitorFetchPatch'
import { isAppShellRoute } from '@/lib/platformUtils'

const SMART_TASK_NAV_OVERRIDE_KEY = 'smart_task_navigation_override';

// Lightweight cold-start telemetry hook. Mirrors the SmartTask cold-start logger
// (window.__coldStartLog) so navigation hydration timing shows up in the same
// /tmp/ta_coldstart_logs/coldstart_*.jsonl traces. No-ops outside dev/staging.
let navigationColdStartInitialLogged = false;
function navigationColdStartLog(tag: string, msg: string, data?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const logger = (window as typeof window & {
    __coldStartLog?: (tag: string, msg: string, data?: unknown) => void;
  }).__coldStartLog;
  if (typeof logger !== 'function') return;
  try {
    logger(`NAVIGATION_${tag}`, msg, data);
  } catch {
    // Diagnostics must never break the app.
  }
}

function getStoredCurrentClientIdForNavigation(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return getDemoAwareExpectedClientId(localStorage.getItem('current_client_id'));
  } catch {
    return null;
  }
}

function isNavigationForCurrentClient(data: NonNullable<NavigationResponse['data']>): boolean {
  const expectedClientId = getStoredCurrentClientIdForNavigation();
  const responseClientId = data.client_info?.id != null ? String(data.client_info.id) : null;
  return !expectedClientId || !responseClientId || expectedClientId === responseClientId;
}

type NavigationTile = {
  key: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  href: string;
  sort_order: number;
  allow_mobile_app: boolean;
  user_role: string;
  permissions: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
};

type NavigationStoreState = {
  navigationData: NonNullable<NavigationResponse['data']> | null;
  userClients: ClientInfo[];
  loading: boolean;
  error: string | null;
};

const navigationStoreListeners = new Set<(state: NavigationStoreState) => void>();

let navigationStoreState: NavigationStoreState = {
  navigationData: null,
  userClients: [],
  loading: true,
  error: null,
};

function emitNavigationStoreState(nextState: NavigationStoreState) {
  navigationStoreState = nextState;
  navigationStoreListeners.forEach((listener) => {
    try {
      listener(nextState);
    } catch (error) {
      console.error('[NAVIGATION] Failed to notify subscriber:', error);
    }
  });
}

function setNavigationStoreState(patch: Partial<NavigationStoreState>) {
  emitNavigationStoreState({
    ...navigationStoreState,
    ...patch,
  });
}

function subscribeToNavigationStore(listener: (state: NavigationStoreState) => void) {
  navigationStoreListeners.add(listener);
  listener(navigationStoreState);

  return () => {
    navigationStoreListeners.delete(listener);
  };
}

// Module-scope dedup: prevents the N-component cold-start stampede where every
// mounting consumer of useNavigation() fires its own /api/v1/navigation request.
// Without this, observed cold starts had 11 parallel requests each taking
// 130-193s because they all queued for the same DB pool slots.
let navigationRefreshInFlight: Promise<void> | null = null;
let navigationLastRefreshAt = 0;
const NAVIGATION_MIN_REFRESH_INTERVAL_MS = 30000;
let navigationForcedRefreshInFlight: Promise<void> | null = null;
let navigationRecoveryRetryTimeout: number | null = null;
let navigationRecoveryRetryAttempt = 0;
const NAVIGATION_RECOVERY_RETRY_DELAYS_MS = [750, 2000, 5000] as const;

function resetNavigationRecoveryRetry() {
  if (navigationRecoveryRetryTimeout !== null) {
    clearTimeout(navigationRecoveryRetryTimeout);
    navigationRecoveryRetryTimeout = null;
  }

  navigationRecoveryRetryAttempt = 0;
}

function scheduleNavigationRecoveryRetry(reason: string) {
  if (typeof window === 'undefined') {
    return;
  }

  if (
    navigationRecoveryRetryTimeout !== null
    || navigationRecoveryRetryAttempt >= NAVIGATION_RECOVERY_RETRY_DELAYS_MS.length
  ) {
    return;
  }

  const attempt = navigationRecoveryRetryAttempt + 1;
  const delay = NAVIGATION_RECOVERY_RETRY_DELAYS_MS[navigationRecoveryRetryAttempt];
  navigationRecoveryRetryAttempt += 1;

  console.warn(`[NAVIGATION] Scheduling recovery refresh attempt ${attempt} in ${delay}ms (${reason})`);

  navigationRecoveryRetryTimeout = window.setTimeout(() => {
    navigationRecoveryRetryTimeout = null;
    window.dispatchEvent(new CustomEvent('navigationRefreshRequested', {
      detail: { reason, attempt },
    }));
  }, delay);
}

function isPrivilegedOperationsRole(role?: string | null): boolean {
  return ['owner', 'admin', 'supervisor', 'manager', 'root'].includes((role || '').toLowerCase());
}

function stripTotalOperationsForSmartTaskUsers(
  data: NonNullable<NavigationResponse['data']>,
): NonNullable<NavigationResponse['data']> {
  const navigation_groups = Object.fromEntries(
    Object.entries(data.navigation_groups)
      .map(([groupKey, group]) => {
        const items = group.items.filter((item) => {
          if (item.module_key !== 'total-operations') {
            return true;
          }

          return isPrivilegedOperationsRole(item.user_role);
        });

        return [groupKey, { ...group, items }];
      })
      .filter(([, group]) => Array.isArray((group as { items?: unknown[] }).items) && (group as { items: unknown[] }).items.length > 0)
  ) as NonNullable<NavigationResponse['data']>['navigation_groups'];

  return {
    ...data,
    navigation_groups,
  };
}

function getStoredSmartTaskNavigationOverride(): boolean | null {
  try {
    const value = localStorage.getItem(SMART_TASK_NAV_OVERRIDE_KEY);
    if (value === 'true') return true;
    if (value === 'false') return false;
  } catch {
    return null;
  }

  return null;
}

// SYNC initialization from localStorage - runs BEFORE first render
function getInitialNavigationData(): NonNullable<NavigationResponse['data']> | null {
  try {
    const stored = localStorage.getItem('offline_navigation_data');
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    if (!data.navigation_groups || !data.user_info || !data.client_info) {
      return null;
    }

    if (data.cache_version !== NAVIGATION_CACHE_VERSION) {
      localStorage.removeItem('offline_navigation_data');
      return null;
    }
    
    // Validate client_id matches
    const currentClientId = getDemoAwareExpectedClientId(localStorage.getItem('current_client_id'));
    if (currentClientId && data.client_info?.id) {
      if (String(data.client_info.id) !== currentClientId) {
        return null; // Stale data
      }
    }

    const storedOverride = getStoredSmartTaskNavigationOverride();
    return storedOverride ? stripTotalOperationsForSmartTaskUsers(data) : data;
  } catch {
    return null;
  }
}

export const useNavigation = () => {
  const { user, isAuthenticated } = useAuth()
  const initialNavigationData = typeof window !== 'undefined' ? getInitialNavigationData() : null

  if (typeof window !== 'undefined' && navigationStoreState.navigationData === null && initialNavigationData) {
    navigationStoreState = {
      ...navigationStoreState,
      navigationData: initialNavigationData,
      loading: false,
      error: null,
    }
  }

  // One-shot cold-start telemetry: did navigation hydrate from localStorage on
  // the first useNavigation() invocation of this app session, or are we going
  // to have to wait for the API? This pinpoints whether the residual cold-start
  // gap is "no cache" vs. "cache present but API still in flight".
  if (typeof window !== 'undefined' && !navigationColdStartInitialLogged) {
    navigationColdStartInitialLogged = true;
    const hydratedFromStore = navigationStoreState.navigationData !== null && !initialNavigationData;
    const hydratedFromLocalStorage = initialNavigationData !== null;
    let rawStoredPresent = false;
    let rawStoredClientId: string | null = null;
    let currentClientId: string | null = null;
    try {
      rawStoredPresent = !!localStorage.getItem('offline_navigation_data');
      currentClientId = localStorage.getItem('current_client_id');
      if (rawStoredPresent) {
        const parsed = JSON.parse(localStorage.getItem('offline_navigation_data') as string);
        rawStoredClientId = parsed?.client_info?.id != null ? String(parsed.client_info.id) : null;
      }
    } catch {
      // ignore
    }
    navigationColdStartLog(
      'HYDRATE',
      hydratedFromLocalStorage
        ? 'Navigation hydrated from localStorage on first hook init'
        : hydratedFromStore
        ? 'Navigation hydrated from in-memory store on first hook init'
        : 'No cached navigation on first hook init — will wait for API',
      {
        hydratedFromLocalStorage,
        hydratedFromStore,
        rawStoredPresent,
        rawStoredClientId,
        currentClientId,
      },
    );
  }
  
  // Initialize from localStorage IMMEDIATELY - no loading state needed if cached
  const [navigationData, setNavigationData] = useState<NonNullable<NavigationResponse['data']> | null>(() => navigationStoreState.navigationData)
  const navigationDataRef = useRef(navigationData)
  navigationDataRef.current = navigationData
  const [userClients, setUserClients] = useState<ClientInfo[]>(() => navigationStoreState.userClients)
  // Only show loading if we have NO cached data
  const [loading, setLoading] = useState(() => navigationStoreState.loading)
  const [error, setError] = useState<string | null>(() => navigationStoreState.error)
  
  // Prevent multiple simultaneous refreshes (per-instance flag retained for legacy
  // callers; module-scope navigationRefreshInFlight enforces the real cross-hook
  // singleton.)
  const refreshInProgressRef = useRef(false)

  // Background refresh - never blocks UI. Coalesces concurrent calls across all
  // useNavigation() consumers via module-scope navigationRefreshInFlight so a
  // cold start with N mounting components only ever issues a single network
  // request.
  const refreshInBackground = useCallback(async () => {
    if (!isAuthenticated) return;
    // Navigation/clients are app-shell data — on public pages there is no
    // consumer for them, and stale tokens would just 401.
    if (typeof window !== 'undefined' && !isAppShellRoute(window.location.pathname)) return;
    if (navigationRefreshInFlight) {
      // Reuse the in-flight request — every concurrent caller awaits the same promise.
      return navigationRefreshInFlight;
    }
    if (Date.now() - navigationLastRefreshAt < NAVIGATION_MIN_REFRESH_INTERVAL_MS) return;

    refreshInProgressRef.current = true;
    navigationLastRefreshAt = Date.now();
    const refreshStartedAt = Date.now();
    navigationColdStartLog('REFRESH_START', 'Background navigation refresh starting', {
      hadCachedNavigation: navigationDataRef.current !== null,
    });

    const inflight = (async () => {
      try {
        // Load navigation and clients in parallel
        const [navData, clientsData] = await Promise.all([
          navigationService.getUserNavigation(),
          navigator.onLine ? clientService.getUserClients().catch(() => null) : Promise.resolve(null)
        ]);

        if (!isNavigationForCurrentClient(navData)) {
          navigationColdStartLog('REFRESH_STALE', 'Ignored stale navigation refresh response after client switch', {
            responseClientId: navData.client_info?.id,
            currentClientId: getStoredCurrentClientIdForNavigation(),
          });
          return;
        }

        setNavigationData(navData);
        setNavigationStoreState({ navigationData: navData, error: null });
        resetNavigationRecoveryRetry();
        if (clientsData?.available_clients) {
          setUserClients(clientsData.available_clients);
          setNavigationStoreState({ userClients: clientsData.available_clients });
        }
        setError(null);
        setNavigationStoreState({ error: null });

        // COLD START FIX: Explicitly save to localStorage to ensure it's available
        // for synchronous hydration on the next cold start. navigationService.fetchFromAPI()
        // also saves, but cold-start logs show rawStoredPresent=false on every session —
        // this belt-and-suspenders write ensures the data persists for instant hydration.
        try {
          const navJson = JSON.stringify({
            ...navData,
            cache_version: NAVIGATION_CACHE_VERSION,
            last_synced: new Date().toISOString(),
          });
          localStorage.setItem('offline_navigation_data', navJson);
          navigationColdStartLog('LOCALSTORAGE_SAVE', 'Navigation data saved to localStorage', {
            dataSize: navJson.length,
          });
        } catch (saveErr) {
          navigationColdStartLog('LOCALSTORAGE_SAVE_FAILED', 'Failed to save navigation to localStorage', {
            error: String(saveErr),
          });
        }

        // COLD START FIX: Also persist to offlineDb (IndexedDB) for offline resilience.
        // This runs in the background — don't block the UI on it.
        try {
          const { default: offlineDb } = await import('@/lib/offline/offlineDb');
          if (offlineDb) {
            const clientId = navData.client_info?.id || 0;
            await offlineDb.meta.put({
              key: 'offline_navigation_data',
              domain: 'app-shell',
              client_id: clientId,
              updated_at: new Date().toISOString(),
              payload: JSON.stringify({
                ...navData,
                cache_version: NAVIGATION_CACHE_VERSION,
              }),
            });
          }
        } catch {
          // Best effort — localStorage above is the primary cold-start cache
        }

        navigationColdStartLog('REFRESH_READY', 'Background navigation refresh succeeded', {
          durationMs: Date.now() - refreshStartedAt,
          moduleCount: Object.values(navData.navigation_groups || {}).reduce(
            (sum, g) => sum + (Array.isArray((g as NavigationGroup).items) ? (g as NavigationGroup).items.length : 0),
            0,
          ),
        });
      } catch (err) {
        const isExpectedDemoTransitionError = err instanceof ApiError
          && err.status === 403
          && err.message === 'User does not have access to this client'
          && typeof window !== 'undefined'
          && isOperationsDemoTransitionActive(window.location.pathname);

        if (!isExpectedDemoTransitionError && !isSilentFetchError(err)) {
          console.warn('[NAVIGATION] Background refresh failed:', err);
        }
        navigationColdStartLog('REFRESH_FAILED', 'Background navigation refresh failed', {
          durationMs: Date.now() - refreshStartedAt,
          expectedDemoTransition: isExpectedDemoTransitionError,
        });
        // Don't set error or clear data - keep existing data
      } finally {
        refreshInProgressRef.current = false;
        navigationRefreshInFlight = null;
        setLoading(false);
        setNavigationStoreState({ loading: false });
      }
    })();

    navigationRefreshInFlight = inflight;
    return inflight;
  }, [isAuthenticated]);

  const refreshNavigation = useCallback(async () => {
    if (navigationForcedRefreshInFlight) {
      return navigationForcedRefreshInFlight;
    }

    const forcedRefreshPromise = (async () => {
      resetNavigationRecoveryRetry();
      navigationService.clearCache();
      navigationRefreshInFlight = null; // Do not await a pre-switch navigation request.
      navigationLastRefreshAt = 0; // Allow immediate refresh
      await refreshInBackground();
    })();

    navigationForcedRefreshInFlight = forcedRefreshPromise.finally(() => {
      navigationForcedRefreshInFlight = null;
    });

    return navigationForcedRefreshInFlight;
  }, [refreshInBackground]);

  // Intentionally omit `navigationData` from deps: subscribing and calling refresh should run when
  // auth/refresh identity changes, not on every API-driven navigation update (that retriggers the
  // effect and can contribute to update loops with setNavigationData). Read latest value via ref.
  useEffect(() => {
    return subscribeToNavigationStore((state) => {
      setNavigationData(state.navigationData)
      setUserClients(state.userClients)
      setLoading(state.loading)
      setError(state.error)
    })
  }, [])

  useEffect(() => {
    if (!isAuthenticated || navigationDataRef.current) {
      return;
    }

    let cancelled = false;

    const hydrateFromOfflineCache = async () => {
      try {
        const cachedNavigation = await navigationService.getCachedNavigationData();
        if (!cachedNavigation || cancelled || !isNavigationForCurrentClient(cachedNavigation)) {
          return;
        }

        setNavigationData(cachedNavigation);
        setLoading(false);
        setError(null);
        resetNavigationRecoveryRetry();
        setNavigationStoreState({
          navigationData: cachedNavigation,
          loading: false,
          error: null,
        });

        // COLD START FIX: Mirror IndexedDB navigation data to localStorage so the
        // next cold start can hydrate synchronously via getInitialNavigationData().
        // This fixes the root cause of slow cold starts: localStorage was always
        // empty (rawStoredPresent=false) even though IndexedDB had the data.
        try {
          localStorage.setItem('offline_navigation_data', JSON.stringify({
            ...cachedNavigation,
            cache_version: NAVIGATION_CACHE_VERSION,
            last_synced: new Date().toISOString(),
          }));
          navigationColdStartLog('IDB_TO_LS_MIRROR', 'Mirrored navigation data from IndexedDB to localStorage');
        } catch {
          // Best effort — IndexedDB is the source of truth
        }
      } catch (err) {
        console.warn('[NAVIGATION] Failed to hydrate cached navigation from IndexedDB:', err);
      }
    };

    void hydrateFromOfflineCache();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) {
      resetNavigationRecoveryRetry();
      setNavigationData(null);
      setUserClients([]);
      setLoading(false);
      setError(null);
      setNavigationStoreState({
        navigationData: null,
        userClients: [],
        loading: false,
        error: null,
      });
      return;
    }

    if (navigationDataRef.current) {
      setNavigationStoreState({ loading: false });
      void refreshInBackground();
    } else {
      setLoading(true);
      setNavigationStoreState({ loading: true, error: null });
      void refreshInBackground();
    }
    
    // Listen for background updates
    const handleUpdate = (event: CustomEvent) => {
      setNavigationData(event.detail);
      resetNavigationRecoveryRetry();
      setNavigationStoreState({ navigationData: event.detail, loading: false, error: null });
    };
    window.addEventListener('navigationDataUpdated', handleUpdate as EventListener);

    const handleRefreshRequested = () => {
      refreshNavigation().catch((err) => {
        console.warn('[NAVIGATION] Refresh request failed:', err);
      });
    };
    window.addEventListener('navigationRefreshRequested', handleRefreshRequested);

    const handleClientSwitched = (event: Event) => {
      const detail = (event as CustomEvent<{ clientId?: number }>).detail;
      const nextClientId = detail?.clientId != null ? String(detail.clientId) : getStoredCurrentClientIdForNavigation();
      const activeNavigationClientId = navigationDataRef.current?.client_info?.id != null
        ? String(navigationDataRef.current.client_info.id)
        : null;

      resetNavigationRecoveryRetry();

      if (nextClientId && activeNavigationClientId && nextClientId !== activeNavigationClientId) {
        setNavigationData(null);
        setNavigationStoreState({ navigationData: null, loading: true, error: null });
      } else {
        setLoading(true);
        setNavigationStoreState({ loading: true, error: null });
      }

      refreshNavigation().catch((err) => {
        console.warn('[NAVIGATION] Client switch refresh failed:', err);
        scheduleNavigationRecoveryRetry('client-switched');
      });
    };
    window.addEventListener('client-switched', handleClientSwitched as EventListener);
    
    return () => {
      window.removeEventListener('navigationDataUpdated', handleUpdate as EventListener);
      window.removeEventListener('navigationRefreshRequested', handleRefreshRequested);
      window.removeEventListener('client-switched', handleClientSwitched as EventListener);
    };
  }, [isAuthenticated, refreshInBackground, refreshNavigation]);

  useEffect(() => {
    if (!isAuthenticated) {
      resetNavigationRecoveryRetry();
      return;
    }

    if (navigationData && isNavigationForCurrentClient(navigationData)) {
      resetNavigationRecoveryRetry();
      return;
    }

    if (loading) {
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    if (!getStoredCurrentClientIdForNavigation()) {
      return;
    }

    scheduleNavigationRecoveryRetry('navigation-data-missing');
  }, [isAuthenticated, loading, navigationData]);

  // Refresh navigation data when the device comes back online after being offline.
  // This ensures module visibility and navigation data are up-to-date after a
  // reconnect, fixing CapacitorFooter showing no modules after offline-launch redirect.
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleOnline = () => {
      navigationLastRefreshAt = 0; // Allow immediate refresh bypassing throttle
      void refreshInBackground();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isAuthenticated, refreshInBackground]);

  // Memoized role from navigation data
  const rawUserRole = navigationData?.user_info?.role || user?.role || 'guest';
  const userRole = normalizeAdminRole(rawUserRole) ?? rawUserRole;
  
  // Role checks - memoized
  const isAdmin = useMemo(() => ['admin', 'super_admin', 'system_admin', 'owner', 'root'].includes(userRole), [userRole]);
  const isManager = useMemo(() => ['manager', 'admin', 'super_admin', 'system_admin', 'owner', 'root'].includes(userRole), [userRole]);
  const isSupervisor = useMemo(() => ['supervisor', 'manager', 'admin', 'super_admin', 'system_admin', 'owner', 'root'].includes(userRole), [userRole]);
  const isEmployee = useMemo(() => userRole === 'employee', [userRole]);
  
  // Module access check
  const canAccessModule = useCallback((moduleKey: string): boolean => {
    if (!navigationData) return false;
    return navigationService.canAccessModule(navigationData, moduleKey);
  }, [navigationData]);
  
  const getAccessibleModules = useCallback((): string[] => {
    if (!navigationData) return [];
    return navigationService.getAccessibleModules(navigationData);
  }, [navigationData]);
  
  const getModulesByCategory = useCallback(() => {
    if (!navigationData) return {};
    return navigationService.transformToLegacyFormat(navigationData);
  }, [navigationData]);
  
  const navigationGroups = useMemo(() => {
    if (!navigationData) return {};
    return navigationService.getNavigationByGroup(navigationData);
  }, [navigationData]);

  /**
   * Get modules formatted for dashboard tiles
   * Returns modules with proper icons, descriptions, and routes
   */
  const getModuleTiles = useCallback(() => {
    if (!navigationData?.navigation_groups) return []
    
    const modules: NavigationTile[] = []

    Object.values(navigationData.navigation_groups).forEach((group: NavigationGroup) => {
      group.items?.forEach((item: NavigationItem) => {
        // Skip hidden items (e.g. total-attendance merged into total-workforce on the HR bundle tier)
        if (item.hidden) return;
        // Skip browse_modules if it exists in the API data (we'll add it at the end)
        if (item.module_key === 'browse_modules') return;
        
        // Skip items without a valid module_key to prevent null key warnings
        if (!item.module_key) return;
        
        modules.push({
          key: item.module_key,
          title: item.label,
          description: item.description || '',
          icon: item.icon || '📦',
          category: group.label || 'General',
          href: `/client-zone/modules/${item.module_key}`, // Always use consistent route format
          sort_order: item.sort_order || 999, // Default to high number if not set
          allow_mobile_app: item.allow_mobile_app ?? false,
          user_role: item.user_role || 'user',
          permissions: item.user_permissions || {
            can_view: true,
            can_create: false,
            can_edit: false,
            can_delete: false,
            can_admin: false
          }
        })
      })
    })

    // Sort modules by sort_order (ascending), then by title alphabetically as secondary sort
    const sortedModules = modules.sort((a, b) => {
      if (a.sort_order !== b.sort_order) {
        return a.sort_order - b.sort_order;
      }
      return a.title.localeCompare(b.title);
    })
    
    // Add Browse Modules as the last item
    sortedModules.push({
      key: 'browse_modules',
      title: 'Browse Modules',
      description: 'Explore all available modules and services in our catalog',
      icon: '🔍',
      category: 'General',
      href: '/products',
      sort_order: 9999, // Always last
      allow_mobile_app: false,
      user_role: 'user',
      permissions: {
        can_view: true,
        can_create: false,
        can_edit: false,
        can_delete: false,
        can_admin: false
      }
    })
    
    return sortedModules
  }, [navigationData])

  /**
   * Get mobile-enabled modules sorted by sort_order
   * These are modules with allow_mobile_app=true that the user has access to
   * Used for Capacitor dashboard rendering
   */
  const getMobileModules = useCallback(() => {
    const allModules = getModuleTiles()
    return allModules
      .filter(m => m.allow_mobile_app && m.key !== 'browse_modules')
      .sort((a, b) => a.sort_order - b.sort_order)
  }, [getModuleTiles])

  /**
   * Check if user has only user-level access (not admin/supervisor) for a module
   */
  const isUserLevelOnly = useCallback((moduleKey: string): boolean => {
    if (!navigationData?.navigation_groups) return true
    
    for (const group of Object.values(navigationData.navigation_groups)) {
      const item = group.items.find((item: NavigationItem) => item.module_key === moduleKey)
      if (item) {
        const userRole = item.user_role || 'user'
        // User is admin/supervisor if role is one of these
        const isAdminOrSupervisor = ['owner', 'admin', 'supervisor', 'manager', 'root'].includes(userRole)
        return !isAdminOrSupervisor
      }
    }
    return true // Default to user-level if not found
  }, [navigationData])

  /**
   * Get quick actions based on user role and accessible modules
   * Returns contextual actions for the sidebar
   */
  const getQuickActions = useCallback(() => {
    if (!navigationData?.navigation_groups) return []

    const actions: Array<{
      id: string
      label: string
      icon: string
      href: string
      color?: string
    }> = []

    // Common actions for all users
    const commonModules = ['total_support', 'total_communications']
    
    Object.values(navigationData.navigation_groups).forEach((group: NavigationGroup) => {
      group.items?.forEach((item: NavigationItem) => {
        if (commonModules.includes(item.module_key)) {
          actions.push({
            id: item.module_key,
            label: item.label,
            icon: item.icon || '📦',
            href: item.route
          })
        }
      })
    })

    // Admin-specific actions
    if (isAdmin) {
      Object.values(navigationData.navigation_groups).forEach((group: NavigationGroup) => {
        group.items?.forEach((item: NavigationItem) => {
          if (['root'].includes(item.module_key)) {
            actions.push({
              id: item.module_key,
              label: item.label,
              icon: item.icon || '⚙️',
              href: item.route,
              color: 'admin'
            })
          }
        })
      })
    }

    return actions
  }, [navigationData, isAdmin])

  /**
   * Get header navigation items (top-level modules)
   * Returns limited set of primary modules for header
   */
  const getHeaderNavItems = useCallback(() => {
    if (!navigationData?.navigation_groups) return []

    const items: Array<{
      key: string
      label: string
      href: string
      icon: string
      isActive?: boolean
    }> = []

    // Get up to 6 primary modules for header
    let count = 0
    const maxItems = 6

    Object.values(navigationData.navigation_groups).forEach((group: NavigationGroup) => {
      if (count >= maxItems) return
      
      group.items?.forEach((item: NavigationItem) => {
        if (count >= maxItems) return
        
        // Prioritize certain modules for header
        const priority = ['total_accounts', 'total_workforce', 'total_attendance', 'total_support', 'total_communications']
        
        if (priority.includes(item.module_key) || count < maxItems) {
          items.push({
            key: item.module_key,
            label: item.label,
            href: item.route,
            icon: item.icon || '📦'
          })
          count++
        }
      })
    })

    return items
  }, [navigationData])

  return {
    // User and auth state
    user: navigationData?.user_info || user,
    userRole,
    isAuthenticated,
    userClients, // Add user clients array
    
    // Navigation data
    navigationData,
    navigationGroups,
    
    // Loading state
    loading,
    error,
    
    // Module access methods
    canAccessModule,
    getAccessibleModules,
    getModulesByCategory,
    
    // UI Transform methods (NEW for Phase 1)
    getModuleTiles,
    getQuickActions,
    getHeaderNavItems,
    
    // Mobile/Capacitor helpers
    getMobileModules,
    isUserLevelOnly,
    
    // Role checks
    isAdmin,
    isManager,
    isSupervisor,
    isEmployee,
    
    // Actions
    refreshNavigation
  }
}
